import { api, throwApiError } from "../../../api/api";
import type { UserType } from "../../auth/authApi";
import type { AccessModule } from "../../../utils/accessModules";

export type AdminUserListItem = {
  id: string;
  nomeCompleto: string;
  cpf: string;
  email: string;
  userType: UserType;
  /** Acessos adicionais ao tipo principal (ADR 0024). */
  extraRoles?: UserType[];
  /** Direitos retirados do administrador restrito (ADR 0027). */
  revokedModules?: AccessModule[];
  /** Celular dos avisos no WhatsApp (só dígitos, com o 55). */
  phone?: string | null;
  whatsappOptIn?: boolean;
  storeIds: string[];
  /** Preenchido quando o usuário está desativado (soft delete). */
  deletedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
};

export type ListUsersParams = {
  page?: number;
  limit?: number;
  /** Busca por nome, e-mail ou CPF — resolvida no servidor. */
  q?: string;
  status?: "active" | "deleted" | "all";
  userType?: UserType;
  /** Só os vinculados a esta loja (supervisores). */
  storeId?: string;
};

export type ListUsersResponse = {
  items: AdminUserListItem[];
  page: number;
  limit: number;
  total: number;
};

export type ImportUsersXlsxResult = {
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  problemCount: number;
  created: Array<{
    row: number;
    nomeCompleto: string;
    cpf: string;
    userType: UserType;
    stores: Array<{ storeId: string; storeName: string }>;
  }>;
  skipped: Array<{
    row: number;
    nomeCompleto: string;
    cpf: string;
    reason: string;
  }>;
  problems: Array<{
    row: number;
    nomeCompleto?: string;
    cpf?: string;
    unidade?: string;
    reason: string;
  }>;
};

const normalizeBearer = (token: string): string => {
  const t = String(token ?? "").trim();
  return t.toLowerCase().startsWith("bearer ") ? t.slice(7).trim() : t;
};

/**
 * Listagem paginada com busca **no servidor**.
 *
 * A tela antiga (`EditUserModal`) varria a listagem página a página e filtrava
 * em memória, porque `GET /users` não tinha `q`. Agora tem — uma requisição por
 * busca.
 */
export const listUsersApi = async (
  token: string,
  params: ListUsersParams = {}
): Promise<ListUsersResponse> => {
  try {
    const { data } = await api.get<ListUsersResponse>("/users", {
      headers: { Authorization: `Bearer ${normalizeBearer(token)}` },
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 10,
        ...(params.q?.trim() ? { q: params.q.trim() } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.userType ? { userType: params.userType } : {}),
        ...(params.storeId ? { storeId: params.storeId } : {}),
      },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar usuários.");
    throw error;
  }
};

/**
 * Importação por planilha.
 *
 * Vai por `fetch` com `FormData`, e não pela instância `api`: o axios do projeto
 * fixa `Content-Type: application/json`, que impediria o navegador de escrever o
 * `boundary` do multipart. A base é lida de `api.defaults.baseURL` para que a
 * rota seja exatamente a mesma das demais chamadas.
 */
export const importUsersXlsxApi = async (
  token: string,
  file: File
): Promise<ImportUsersXlsxResult> => {
  const base = String(api.defaults.baseURL ?? "").replace(/\/+$/, "");
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${base}/auth/users/import-xlsx`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${normalizeBearer(token)}`,
      Accept: "application/json",
    },
    body: formData,
  });

  if (!res.ok) {
    const texto = await res.text();
    let mensagem = texto || "Erro ao importar a planilha.";
    try {
      const json = JSON.parse(texto);
      mensagem = Array.isArray(json?.message)
        ? json.message.join(" | ")
        : json?.message ?? mensagem;
    } catch {
      /* resposta não-JSON: fica o texto cru. */
    }
    throw new Error(mensagem);
  }

  return (await res.json()) as ImportUsersXlsxResult;
};
